/**
 * Nepali month index reference:
 *
 * Baisakh  => 0   Jestha  => 1   Asar    => 2   Shrawan => 3
 * Bhadra   => 4   Aswin   => 5   Kartik  => 6   Mangsir => 7
 * Poush    => 8   Magh    => 9   Falgun  => 10  Chaitra => 11
 */

// ─── Public types ─────────────────────────────────────────────────────────────

export type Language = "en" | "np";

/** Bikram Sambat date
 * 0-indexed month (0 = Baisakh)
 * 0-indexed day (0 = Sunday)
 */
export interface BSDate {
	year: number;
	month: number;
	date: number;
	day: number;
}

/** Anno Domini (Gregorian) date */
export interface ADDate {
	year: number;
	month: number;
	date: number;
	day: number;
}

/** Result returned by convertFromAD / convertFromBS */
export interface ConvertResult {
	AD: ADDate;
	BS: BSDate;
}

/** Minimal BS date input — day is not required for input */
export interface BSDateInput {
	year: number;
	month: number;
	date: number;
}

// ─── Internal types ───────────────────────────────────────────────────────────

/** [totalDaysInMonth, cumulativeDaysBeforeMonth] */
type MonthEntry = [totalDays: number, completedDays: number];

/** [totalDaysInYear, cumulativeDaysBeforeYear] */
type YearEntry = [totalDays: number, completedDays: number];

interface FormatLanguageData {
	day: { short: string[]; long: string[] };
	month: { short: string[]; long: string[] };
	date: string[];
}

// ─── Year/month days table ────────────────────────────────────────────────────

/**
 * Days per month for each BS year, starting from 2050 BS.
 * Each inner array has 12 entries (Baisakh → Chaitra).
 *
 * MAINTENANCE: When adding a new year, append one row here.
 * MAX_DAY is derived automatically — no other constant needs updating.
 */
const yearMonthDaysArray: number[][] = [
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31], // 2050
	[31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30], // 2051
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2052
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30], // 2053
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31], // 2054
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2055
	[31, 31, 32, 31, 32, 30, 30, 29, 30, 29, 30, 30], // 2056
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31], // 2057
	[30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31], // 2058
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2059
	[31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30], // 2060
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31], // 2061
	[30, 32, 31, 32, 31, 31, 29, 30, 29, 30, 29, 31], // 2062
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2063
	[31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30], // 2064
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31], // 2065
	[31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 29, 31], // 2066
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2067
	[31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30], // 2068
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31], // 2069
	[31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30], // 2070
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2071
	[31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30], // 2072
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31], // 2073
	[31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30], // 2074
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2075
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30], // 2076
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31], // 2077
	[31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30], // 2078
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2079
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30], // 2080
	[31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31], // 2081
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2082
	[31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30], // 2083
	[31, 31, 32, 31, 31, 30, 30, 30, 29, 30, 30, 30], // 2084
	[31, 32, 31, 32, 30, 31, 30, 30, 29, 30, 30, 30], // 2085
	[30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30], // 2086
	[31, 31, 32, 31, 31, 31, 30, 30, 29, 30, 30, 30], // 2087
	[30, 31, 32, 32, 30, 31, 30, 30, 29, 30, 30, 30], // 2088
	[30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30], // 2089
	[30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30], // 2090
];

export const START_YEAR = 2050;

/** Last BS year covered by yearMonthDaysArray — derived automatically. */
export const END_YEAR = START_YEAR + yearMonthDaysArray.length - 1;

const ERR_MESSAGE = `The date doesn't fall within ${START_YEAR}/01/01 - ${END_YEAR}/12/30`;

const beginNepali = {
	year: START_YEAR,
	month: 0,
	date: 1,
	day: 2,
} as const;

/**
 * English epoch: April 13 1993 (one day before Baisakh 01 2050 BS)
 */
const beginEnglish = {
	year: 1993,
	month: 3,
	date: 12,
	day: 2,
} as const;

// ─── Memoised lookup tables (single pass) ────────────────────────────────────

/**
 * Build monthDaysMappings, yearDaysMapping, and MAX_DAY in one pass over
 * yearMonthDaysArray instead of three separate iterations.
 */
const monthDaysMappings: MonthEntry[][] = [];
const yearDaysMapping: YearEntry[] = [];
let _totalDays = 0;

for (const yearMappings of yearMonthDaysArray) {
	let monthSum = 0;
	const monthEntries: MonthEntry[] = yearMappings.map(
		(monthDays): MonthEntry => {
			const entry: MonthEntry = [monthDays, monthSum];
			monthSum += monthDays;
			return entry;
		},
	);
	monthDaysMappings.push(monthEntries);
	yearDaysMapping.push([monthSum, _totalDays]);
	_totalDays += monthSum;
}

/**
 * Maximum epoch day — derived from the single-pass total above.
 * Appending a new year row to yearMonthDaysArray is the only required maintenance.
 */
const MAX_DAY: number = _totalDays;

const MIN_DAY = 1;

// Structural integrity check — validates each year has exactly 12 month entries.
// This catches accidental row truncation or misalignment at module load time.
if (yearMonthDaysArray.some((year) => year.length !== 12)) {
	throw new Error(
		"Invalid yearMonthDaysArray: every year must have exactly 12 month entries.",
	);
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

const COMPLETED_DAYS = 1 as const;
const TOTAL_DAYS = 0 as const;

function getYearIndex(year: number): number {
	return year - beginNepali.year;
}

function getYearFromIndex(yearIndex: number): number {
	return yearIndex + beginNepali.year;
}

function mod(m: number, val: number): number {
	while (val < 0) {
		val += m;
	}
	return val % m;
}

// ─── Format data ─────────────────────────────────────────────────────────────

const formatObj: Record<Language, FormatLanguageData> = {
	en: {
		day: {
			short: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
			long: [
				"Sunday",
				"Monday",
				"Tuesday",
				"Wednesday",
				"Thursday",
				"Friday",
				"Saturday",
			],
		},
		month: {
			short: [
				"Bai",
				"Jes",
				"Asa",
				"Shr",
				"Bhd",
				"Asw",
				"Kar",
				"Man",
				"Pou",
				"Mag",
				"Fal",
				"Cha",
			],
			long: [
				"Baisakh",
				"Jestha",
				"Asar",
				"Shrawan",
				"Bhadra",
				"Aswin",
				"Kartik",
				"Mangsir",
				"Poush",
				"Magh",
				"Falgun",
				"Chaitra",
			],
		},
		date: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
	},
	np: {
		day: {
			short: ["आइत", "सोम", "मंगल", "बुध", "बिहि", "शुक्र", "शनि"],
			long: [
				"आइतबार",
				"सोमबार",
				"मंगलबार",
				"बुधबार",
				"बिहिबार",
				"शुक्रबार",
				"शनिबार",
			],
		},
		month: {
			short: [
				"बै",
				"जे",
				"अ",
				"श्रा",
				"भा",
				"आ",
				"का",
				"मं",
				"पौ",
				"मा",
				"फा",
				"चै",
			],
			long: [
				"बैशाख",
				"जेठ",
				"असार",
				"श्रावण",
				"भाद्र",
				"आश्विन",
				"कार्तिक",
				"मंसिर",
				"पौष",
				"माघ",
				"फाल्गुण",
				"चैत्र",
			],
		},
		date: ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"],
	},
};

// ─── Core conversion functions ────────────────────────────────────────────────

/**
 * Calculates days elapsed from the BS epoch for a given BS date.
 * @param year  BS year (2050–range end)
 * @param month Month index (0–11); may be negative or > 11 for overflow arithmetic
 * @param date  Day of month; may be negative or overflow for arithmetic
 * @returns Number of days since epoch
 */
function findPassedDays(year: number, month: number, date: number): number {
	try {
		const yearIndex = getYearIndex(year);
		const pastYearDays = yearDaysMapping[yearIndex][COMPLETED_DAYS];
		const extraMonth = mod(12, month);
		const extraYear = Math.floor(month / 12);

		const pastMonthDays =
			yearDaysMapping[yearIndex + extraYear][COMPLETED_DAYS] -
			pastYearDays +
			monthDaysMappings[yearIndex + extraYear][extraMonth][COMPLETED_DAYS];

		const daysPassed = pastYearDays + pastMonthDays + date;

		if (daysPassed < MIN_DAY || daysPassed > MAX_DAY) {
			throw new Error();
		}
		return daysPassed;
	} catch (error) {
		console.error("----findPassedDays------", error);
		throw new Error(ERR_MESSAGE);
	}
}

/**
 * Maps a days-since-epoch count back to a BS { year, month, date } object.
 * @param daysPassed Days since BS epoch
 */
function mapDaysToDate(daysPassed: number): BSDateInput {
	if (daysPassed < MIN_DAY || daysPassed > MAX_DAY) {
		throw new Error(
			`The epoch difference is not within the boundaries ${MIN_DAY} - ${MAX_DAY}`,
		);
	}

	const yearIndex = yearDaysMapping.findIndex(
		(year) =>
			daysPassed > year[COMPLETED_DAYS] &&
			daysPassed <= year[COMPLETED_DAYS] + year[TOTAL_DAYS],
	);
	const monthRemainder =
		daysPassed - yearDaysMapping[yearIndex][COMPLETED_DAYS];
	const monthIndex = monthDaysMappings[yearIndex].findIndex(
		(month) =>
			monthRemainder > month[COMPLETED_DAYS] &&
			monthRemainder <= month[COMPLETED_DAYS] + month[TOTAL_DAYS],
	);
	const date =
		monthRemainder - monthDaysMappings[yearIndex][monthIndex][COMPLETED_DAYS];

	return {
		year: getYearFromIndex(yearIndex),
		month: monthIndex,
		date,
	};
}

/**
 * Calculates days elapsed from the AD epoch for a given AD Date.
 * @param year  AD year
 * @param month Month index (0–11)
 * @param date  Day of month
 */
function findPassedDaysAD(year: number, month: number, date: number): number {
	try {
		const timeDiff =
			Date.UTC(year, month, date) -
			Date.UTC(beginEnglish.year, beginEnglish.month, beginEnglish.date);

		const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

		if (diffDays < MIN_DAY || diffDays > MAX_DAY) {
			throw new Error();
		}
		return diffDays;
	} catch (error) {
		console.error("----findPassedDaysAD------", error);
		throw new Error(ERR_MESSAGE);
	}
}

/**
 * Maps a days-since-epoch count back to an AD date object.
 * @param daysPassed Days since AD epoch
 */
function mapDaysToDateAD(daysPassed: number): ADDate {
	const mappedDate = new Date(
		Date.UTC(
			beginEnglish.year,
			beginEnglish.month,
			beginEnglish.date + daysPassed,
		),
	);
	return {
		year: mappedDate.getUTCFullYear(),
		month: mappedDate.getUTCMonth(),
		date: mappedDate.getUTCDate(),
		day: mappedDate.getUTCDay(),
	};
}

// ─── Exports ──────────────────────────────────────────────────────────────────

/**
 * Convert a BS date object to both AD and BS representations.
 * @param bsDateObject Input BS date (year, month, date)
 * @returns ConvertResult with both AD and BS (including day)
 */
export function convertFromBS(bsDateObject: BSDateInput): ConvertResult {
	try {
		const daysPassed = findPassedDays(
			bsDateObject.year,
			bsDateObject.month,
			bsDateObject.date,
		);
		const BS = mapDaysToDate(daysPassed);
		const AD = mapDaysToDateAD(daysPassed);
		return {
			AD,
			BS: { ...BS, day: AD.day },
		};
	} catch (error) {
		console.error(error);
		throw new Error(ERR_MESSAGE);
	}
}

/**
 * Convert a JS Date (AD) to both AD and BS representations.
 * @param adDateObject A valid JavaScript Date object
 * @returns ConvertResult with both AD and BS (including day)
 */
export function convertFromAD(adDateObject: Date): ConvertResult {
	try {
		const daysPassed = findPassedDaysAD(
			adDateObject.getFullYear(),
			adDateObject.getMonth(),
			adDateObject.getDate(),
		);
		const BS = mapDaysToDate(daysPassed);
		const AD = mapDaysToDateAD(daysPassed);
		return {
			AD,
			BS: { ...BS, day: AD.day },
		};
	} catch (error) {
		console.error(error);
		throw new Error(ERR_MESSAGE);
	}
}

/**
 * Format a BS date using a format string.
 *
 * Tokens: YYYY YYY YY | M MM MMM MMMM | D DD | d dd ddd
 * Use `\\` to escape any token character.
 *
 * @param bsDate    BS date object
 * @param formatString  Format string
 * @param language  "en" (default) or "np"
 */
/**
 * Maps a numeric string to its digit characters in the target language.
 * Extracted from `format` to avoid recreating the closure on every call.
 */
function mapLanguageNumber(n: string, language: Language): string {
	return n
		.split("")
		.map((ch) => formatObj[language].date[parseInt(ch, 10)])
		.join("");
}

export function format(
	bsDate: BSDate,
	formatString: string,
	language: Language = "en",
): string {
	const mapNum = (n: string): string => mapLanguageNumber(n, language);

	return formatString
		.replace(
			/((\\[MDYd])|D{1,2}|M{1,4}|Y{2,4}|d{1,3})/g,
			(match, _, matchedString?: string) => {
				switch (match) {
					case "D":
						return mapNum(bsDate.date.toString());
					case "DD":
						return mapNum(bsDate.date.toString().padStart(2, "0"));
					case "M":
						return mapNum((bsDate.month + 1).toString());
					case "MM":
						return mapNum((bsDate.month + 1).toString().padStart(2, "0"));
					case "MMM":
						return formatObj[language].month.short[bsDate.month];
					case "MMMM":
						return formatObj[language].month.long[bsDate.month];
					case "YY":
						return mapNum(bsDate.year.toString().slice(-2));
					case "YYY":
						return mapNum(bsDate.year.toString().slice(-3));
					case "YYYY":
						return mapNum(bsDate.year.toString());
					case "d":
						return mapNum((bsDate.day ?? 0).toString());
					case "dd":
						return formatObj[language].day.short[bsDate.day ?? 0];
					case "ddd":
						return formatObj[language].day.long[bsDate.day ?? 0];
					default:
						return (matchedString ?? "").replace("/", "");
				}
			},
		)
		.replace(/\\/g, "");
}

/**
 * Returns the number of days in a given BS year/month using the pre-computed
 * lookup table. Safe for all valid year/month combinations including the last
 * year's last month — no overflow arithmetic used.
 *
 * @param year  BS year (START_YEAR … END_YEAR)
 * @param month Month index (0–11)
 * @returns Number of days, or 0 if the year/month is out of range
 */
export function getDaysInBSMonth(year: number, month: number): number {
	const yearIndex = year - START_YEAR;
	return monthDaysMappings[yearIndex]?.[month]?.[TOTAL_DAYS] ?? 0;
}

/**
 * Parse a BS date string in `YYYY/MM/DD` or `DD/MM/YYYY` format.
 * @param dateString  Date string to parse
 * @returns BSDateInput (year, month index, date)
 */
export function parse(dateString: string): BSDateInput {
	const OFFICIAL_FORMAT =
		/(\d{4})\s*([/-]|\s+)\s*(\d{1,2})\s*([/-]|\s+)\s*(\d{1,2})/;
	const GEORGIAN_FORMAT =
		/(\d{1,2})\s*([/-]|\s+)\s*(\d{1,2})\s*([/-]|\s+)\s*(\d{4})/;

	let match = dateString.match(OFFICIAL_FORMAT);
	if (match !== null) {
		return {
			year: parseInt(match[1], 10),
			month: parseInt(match[3], 10) - 1,
			date: parseInt(match[5], 10),
		};
	}

	match = dateString.match(GEORGIAN_FORMAT);
	if (match !== null) {
		return {
			year: parseInt(match[5], 10),
			month: parseInt(match[3], 10) - 1,
			date: parseInt(match[1], 10),
		};
	}

	throw new Error("Invalid date format");
}
